import { getApplicationFormNotifications, putApplicationFormStatusNotification } from '@/api/Rover/ApplicationFormNotifications';
import type {
  ChannelContent,
  ChannelEnabled,
  ChannelMessageType,
  MessageOption,
  StatusNotificationConfig,
  StatusNotificationSummary,
} from '@/api/Rover/notifications/roverNotifications.types';
import {
  mapFormStatusToConfig,
  mapFormStatusToSummary,
  mapNotificationOptions,
  mapSavedNotificationToConfig,
  toSavePayload,
} from './applicationFormNotificationsMapper';
import type { ApplicationFormStatusCatalogItem } from './statusCatalog';

export type FormNotificationsDraft = { content: ChannelContent; enabled: ChannelEnabled; messageType?: ChannelMessageType };

const dedupeByLabel = (list: StatusNotificationSummary[]) => {
  const seen = new Set<string>();
  return list.filter((s) => {
    if (seen.has(s.statusLabel)) return false;
    seen.add(s.statusLabel);
    return true;
  });
};

export type ApplicationFormStatusListResult = {
  summaries: StatusNotificationSummary[];
  schemaReady: boolean;
  statusCatalog: ApplicationFormStatusCatalogItem[];
  schemaError?: string;
};

export const applicationFormNotificationsClient = {
  async listStatuses(applicationFormId: string): Promise<ApplicationFormStatusListResult> {
    const res = await getApplicationFormNotifications({ applicationFormId });
    const summaries = dedupeByLabel((res.data ?? []).map(mapFormStatusToSummary));
    const statusCatalog = res.meta?.statusCatalog ?? [];
    const schemaReady = res.meta?.schemaReady === true;
    return { summaries, schemaReady, statusCatalog, schemaError: res.meta?.schemaError };
  },

  async loadStatusDetail(applicationFormId: string, statusId: string) {
    const res = await getApplicationFormNotifications({ applicationFormId });
    const row = (res.data ?? []).find((s) => s.id === statusId);
    if (!row) {
      throw new Error('Status not found');
    }
    return {
      config: mapFormStatusToConfig(row),
      options: mapNotificationOptions(row.notification),
    };
  },

  async saveStatus(
    applicationFormId: string,
    statusId: string,
    draft: FormNotificationsDraft,
    options: MessageOption[]
  ): Promise<{ config: StatusNotificationConfig; options: MessageOption[] }> {
    const list = await getApplicationFormNotifications({ applicationFormId });
    const row = (list.data ?? []).find((s) => s.id === statusId);
    if (!row) {
      throw new Error('Status not found');
    }
    const res = await putApplicationFormStatusNotification({
      applicationFormId,
      statusId,
      data: toSavePayload(draft, options),
    });
    return {
      config: mapSavedNotificationToConfig(row, res.data),
      options: mapNotificationOptions(res.data),
    };
  },

  async resetStatus(applicationFormId: string, statusId: string): Promise<{ config: StatusNotificationConfig; options: MessageOption[] }> {
    const empty: FormNotificationsDraft = {
      content: {
        email: { subject: null, body: null },
        sms: { body: null },
        onscreen: { title: null, body: null },
      },
      enabled: { email: true, sms: true, onscreen: true },
    };
    return applicationFormNotificationsClient.saveStatus(applicationFormId, statusId, empty, []);
  },
};
